import { SAUNA_ENQUIRY_OPTIONS } from "@/data/sauna-enquiry-options";
import { useState } from "react";
import { Link } from "wouter";
import { CheckCircle2, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";

type Props = {
  title: string;
  highlight?: string;
  subtitle?: string;
  backgroundImageUrl?: string | null;
  badge?: string;
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

export function SiteHero({ title, highlight, subtitle, backgroundImageUrl, badge = "Trusted AC & HVAC Specialists" }: Props) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", postcode: "", service: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: "callback@request.com",
          phone: form.phone,
          message: `Callback requested. Service: ${form.service || "Not specified"}. Postcode: ${form.postcode || "Not provided"}.`,
          formType: "Callback Request",
          extra: { Service: form.service, Postcode: form.postcode, Page: typeof window !== "undefined" ? window.location.pathname : "" },
        }),
      });
      if (!res.ok) throw new Error("Failed");
      toast({ title: "Callback requested!", description: "We'll call you back shortly." });
      setForm({ name: "", phone: "", postcode: "", service: "" });
    } catch {
      toast({ title: "Something went wrong", description: "Please try again or call 020 8087 0906.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="relative pt-20 pb-32 overflow-hidden bg-slate-900">
        <div className="absolute inset-0 z-0">
          {backgroundImageUrl ? (
            <img
              src={backgroundImageUrl}
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          ) : (
            <div className="absolute inset-0 bg-slate-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/60 to-slate-900/30" />
        </div>

        <div className="container mx-auto px-4 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-[1fr_380px] gap-12 items-center">
            <div className="max-w-2xl">
              <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
                <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-blue-300 font-semibold text-sm mb-6 backdrop-blur-sm border border-white/10">
                  <CheckCircle2 className="w-4 h-4" /> {badge}
                </motion.div>

                <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold font-display leading-[1.1] text-white mb-6">
                  {title}
                  {highlight && (
                    <>
                      {" "}
                      <span className="text-blue-400 relative">
                        {highlight}
                        <span className="absolute bottom-1 left-0 w-full h-3 bg-blue-400/20 -z-10 -rotate-1"></span>
                      </span>
                    </>
                  )}
                </motion.h1>

                {subtitle && (
                  <motion.p variants={fadeInUp} className="text-lg md:text-xl text-white/70 mb-8 max-w-xl leading-relaxed">
                    {subtitle}
                  </motion.p>
                )}

                <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                  <Link href="/quote">
                    <Button size="lg" className="h-14 px-8 text-base rounded-full shadow-lg hover:shadow-primary/25 hover:-translate-y-0.5 transition-all w-full sm:w-auto">
                      Request Free Site Survey
                    </Button>
                  </Link>
                  <a href="tel:02080870906">
                    <Button size="lg" variant="outline" className="h-14 px-8 text-base rounded-full bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20 w-full sm:w-auto">
                      Call 020 8087 0906
                    </Button>
                  </a>
                </motion.div>

                <motion.div variants={fadeInUp} className="mt-12 flex items-center gap-8 border-t border-white/15 pt-8">
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-white font-display">15+</span>
                    <span className="text-sm font-medium text-white/50 uppercase tracking-wider">Years Exp.</span>
                  </div>
                  <Separator orientation="vertical" className="h-12 bg-white/20" />
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-white font-display">24/7</span>
                    <span className="text-sm font-medium text-white/50 uppercase tracking-wider">Support</span>
                  </div>
                  <Separator orientation="vertical" className="h-12 bg-white/20" />
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-white font-display">5.0</span>
                    <div className="flex text-yellow-400 text-sm mt-0.5">
                      {"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="hidden lg:block"
            >
              <form onSubmit={submit} className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border-t-4 border-primary overflow-hidden">
                <div className="p-6 pb-4">
                  <div className="flex items-center gap-3 mb-1">
                    <Phone className="w-5 h-5 text-primary" />
                    <h2 className="text-xl font-bold font-display text-foreground">Request a Callback</h2>
                  </div>
                  <p className="text-sm text-muted-foreground ml-8">Get a free quote within 24 hours</p>
                </div>
                <div className="px-6 pb-6 space-y-3">
                  <input
                    type="text"
                    required
                    placeholder="Your Name"
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full h-12 px-4 rounded-lg bg-slate-100 border border-slate-200 text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                  <input
                    type="tel"
                    required
                    placeholder="Phone Number"
                    value={form.phone}
                    onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    className="w-full h-12 px-4 rounded-lg bg-slate-100 border border-slate-200 text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                  <input
                    type="text"
                    placeholder="Site Address / Postcode"
                    value={form.postcode}
                    onChange={e => setForm(p => ({ ...p, postcode: e.target.value }))}
                    className="w-full h-12 px-4 rounded-lg bg-slate-100 border border-slate-200 text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                  <select
                    aria-label="Service required"
                    value={form.service}
                    onChange={e => setForm(p => ({ ...p, service: e.target.value }))}
                    className="w-full h-12 px-4 rounded-lg bg-slate-100 border border-slate-200 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all appearance-none"
                  >
                    <option value="">Select Service</option>
                    <option value="AC Installation">AC Installation</option>
                    <option value="AC Repair">AC Repair</option>
                    <option value="AC Maintenance">AC Maintenance</option>
                    <option value="Ventilation">Ventilation</option>
                    <option value="Heating">Heating</option>
                    <option value="Ductwork">Ductwork</option>
                    <option value="BMS & Controls">BMS & Controls</option>
                    {SAUNA_ENQUIRY_OPTIONS.map(service => <option key={service} value={service}>{service}</option>)}
                  </select>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-primary/25 disabled:opacity-60"
                  >
                    {submitting ? "Sending..." : "Get Free Quote"} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="bg-[#1a2332] py-3 border-y border-white/10">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-white/70">Our customers say</span>
              <span className="text-white font-bold">Excellent</span>
              <div className="flex text-green-400 text-xs gap-0.5">
                {"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-green-400 font-bold">★</span>
              <span className="text-white font-semibold">Trustpilot</span>
              <span className="text-white/60">4.9 · 576 reviews</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-blue-400 font-bold">G</span>
              <span className="text-white font-semibold">Google Reviews</span>
              <span className="text-white/60">4.8 · 320 reviews</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-orange-400 font-bold">✓</span>
              <span className="text-white font-semibold">Checkatrade</span>
              <span className="text-white/60">9.9 · 105 reviews</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
